import { platform } from './index';

/**
 * Keeping the screen awake while a round is being played.
 *
 * Reading a board is slow, deliberate work: a player can stare at four words for a minute without
 * touching the glass, which is exactly when the phone dims and then locks. Every other genre gets
 * this for free — a constant stream of taps keeps the OS idle timer reset — so the fix has to be
 * explicit here.
 *
 * Two mechanisms, one interface:
 *  - `PlatformBridge.keepAwake` — the shell's native switch (iOS `isIdleTimerDisabled`). Preferred
 *    wherever it exists: it works on every OS version, whereas WebKit only shipped the web API in
 *    16.4 and the app's deployment target is 16.0.
 *  - `navigator.wakeLock` — the Screen Wake Lock API. Covers the web build and the Android TWA
 *    (a TWA is Chrome, which has had the API since 84, and has no JS↔native channel to use
 *    instead). Note that a plain Android WebView does NOT expose it — see
 *    specs/android-webview-migration.md.
 *
 * The whole thing is best-effort. A refusal (iOS Low Power Mode, a hidden document, an old
 * browser) is silent: the round plays exactly as it did before, the screen just dims.
 */

/** The slice of a WakeLockSentinel this module touches. */
export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener?(type: 'release', listener: () => void): void;
}

/** Everything the controller reaches for, injected so the logic is testable without a DOM. */
export interface WakeLockHost {
  /** The shell's native keep-awake switch, where there is one. Wins over `requestLock`. */
  keepAwake?: (on: boolean) => void;
  /** `navigator.wakeLock.request('screen')`, or undefined where the API is absent. */
  requestLock?: () => Promise<WakeLockSentinelLike>;
  /** `document.visibilityState === 'visible'`. */
  isVisible(): boolean;
  /** Subscribe to visibilitychange; returns the detach function. */
  onVisibilityChange(listener: () => void): () => void;
}

export interface WakeLock {
  /** Hold the screen awake. Idempotent — a second call while held does nothing. */
  acquire(): void;
  /** Hand the screen back to the OS. Idempotent. */
  release(): void;
}

export function createWakeLock(host: WakeLockHost): WakeLock {
  /** Only the web API needs managing; a native switch is a plain on/off. */
  const useWebApi = !host.keepAwake && !!host.requestLock;

  let wanted = false;
  let sentinel: WakeLockSentinelLike | null = null;
  let pending = false;
  let detach: (() => void) | null = null;

  const request = (): void => {
    if (!wanted || sentinel || pending || !host.isVisible()) return;
    pending = true;
    host.requestLock!().then(
      (granted) => {
        pending = false;
        // Released while the request was in flight (the round ended, or the app went to the
        // background) — the lock arrived for nobody, so hand it straight back.
        if (!wanted) {
          void granted.release().catch(() => { /* already gone */ });
          return;
        }
        sentinel = granted;
        // The browser releases the lock by itself whenever the page stops being visible. Dropping
        // our reference when that happens is what lets the visibilitychange handler below take a
        // fresh one on return — without it the screen would stay awake only until the first time
        // the player switched apps.
        granted.addEventListener?.('release', () => {
          if (sentinel === granted) sentinel = null;
        });
      },
      () => { pending = false; },   // denied — best-effort, see the module comment
    );
  };

  return {
    acquire(): void {
      if (wanted) return;
      wanted = true;
      host.keepAwake?.(true);
      if (!useWebApi) return;
      detach = host.onVisibilityChange(() => {
        // Spec: the lock is released whenever the document stops being visible. Dropping the
        // reference here as well as on the sentinel's own 'release' event means the retake below
        // never depends on which of the two the browser fires first.
        if (!host.isVisible()) sentinel = null;
        else request();
      });
      request();
    },

    release(): void {
      if (!wanted) return;
      wanted = false;
      host.keepAwake?.(false);
      detach?.();
      detach = null;
      const held = sentinel;
      sentinel = null;
      if (held) void held.release().catch(() => { /* already gone */ });
    },
  };
}

interface WakeLockNavigator {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
}

let instance: WakeLock | null = null;

/**
 * The app's single screen wake lock. Lazy because it reads the platform bridge, which only exists
 * once initPlatform() has run (src/main.ts).
 */
export function screenWakeLock(): WakeLock {
  if (!instance) {
    const nav = navigator as Navigator & WakeLockNavigator;
    instance = createWakeLock({
      keepAwake: platform().keepAwake,
      requestLock: nav.wakeLock ? () => nav.wakeLock!.request('screen') : undefined,
      isVisible: () => document.visibilityState === 'visible',
      onVisibilityChange: (listener) => {
        document.addEventListener('visibilitychange', listener);
        return () => document.removeEventListener('visibilitychange', listener);
      },
    });
  }
  return instance;
}
